import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { PaperAirplaneIcon, StopIcon, FolderIcon } from '@heroicons/react/24/solid';
import { PhotoIcon, MicrophoneIcon, DocumentIcon } from '@heroicons/react/24/outline';
import PaperClipIcon from '../icons/PaperClipIcon';
import XMarkIcon from '../icons/XMarkIcon';
import ModelSelector from '../ModelSelector';
import FolderSelectorPopover from './FolderSelectorPopover';

import { i18nService } from '../../services/i18n';

import { RootState } from '../../store';
import { setDraftPrompt } from '../../store/slices/coworkSlice';


import { CoworkImageAttachment } from '../../types/cowork';
import { getCompactFolderName } from '../../utils/path';
import { uploadFiles, uploadBlob, isAllowedFile } from '../../services/upload';
import { getApiUrl, getAuthToken } from '../../services/endpoints';

declare global {
  interface Window {
    __APP_MODE__?: 'dev' | 'demo';
  }
}

const isDemoMode = (): boolean => typeof window !== 'undefined' && window.__APP_MODE__ === 'demo';

type CoworkAttachment = {
  path: string;
  name: string;
  isImage?: boolean;
  isAudio?: boolean;
  isText?: boolean;
  mimeType?: string;
  serverFilePath?: string;
  previewDataUrl?: string;
  type?: 'image' | 'audio' | 'text' | 'file';
  uploading?: boolean;
};

const INPUT_FILE_LABEL = 'Input File';

const isImageMimeType = (mimeType: string): boolean => {
  return mimeType.startsWith('image/');
};

const isAudioMimeType = (mimeType: string): boolean => {
  return mimeType.startsWith('audio/');
};

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to read file'));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

const getFileNameFromPath = (path: string): string => {
  const parts = path.split(/[/\\]/);
  return parts[parts.length - 1] || path;
};



export interface CoworkPromptInputRef {
  /** Set input value */
  setValue: (value: string) => void;
  /** Focus input */
  focus: () => void;
}

interface CoworkPromptInputProps {
  onSubmit: (prompt: string, skillPrompt?: string, imageAttachments?: CoworkImageAttachment[], audioAttachments?: CoworkImageAttachment[], fileAttachments?: CoworkImageAttachment[]) => void;
  onStop?: () => void;
  isStreaming?: boolean;
  placeholder?: string;
  disabled?: boolean;
  size?: 'normal' | 'large';
  workingDirectory?: string;
  onWorkingDirectoryChange?: (dir: string) => void;
  showFolderSelector?: boolean;
  showModelSelector?: boolean;
  onManageSkills?: () => void;
  onChange?: (value: string) => void;
}

const CoworkPromptInput = React.forwardRef<CoworkPromptInputRef, CoworkPromptInputProps>(
  (props, ref) => {
    const {
      onSubmit,
      onStop,
      isStreaming = false,
      placeholder = 'Enter your task...',
      disabled = false,
      size = 'normal',
      workingDirectory = '',
      onWorkingDirectoryChange,
      showFolderSelector = false,
      showModelSelector = false,
      onChange,
    } = props;
    const dispatch = useDispatch();
    const draftPrompt = useSelector((state: RootState) => state.cowork.draftPrompt);
    const [value, setValue] = useState(draftPrompt);
    const [attachments, setAttachments] = useState<CoworkAttachment[]>([]);
    const [showFolderMenu, setShowFolderMenu] = useState(false);
    const [showFolderRequiredWarning, setShowFolderRequiredWarning] = useState(false);
    const [isDraggingFiles, setIsDraggingFiles] = useState(false);
    const [isAddingFile, setIsAddingFile] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const folderButtonRef = useRef<HTMLButtonElement>(null);
    const dragDepthRef = useRef(0);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
    const pendingAudioRef = useRef<{ serverFilePath: string; name: string; mimeType: string } | null>(null);
    const pendingTranscribedTextRef = useRef<string | null>(null);
    const isStoppingForSendRef = useRef(false);

  // Expose methods to parent component
  React.useImperativeHandle(ref, () => ({
    setValue: (newValue: string) => {
      setValue(newValue);
      // Trigger auto-resize height
      requestAnimationFrame(() => {
        const textarea = textareaRef.current;
        if (textarea) {
          textarea.style.height = 'auto';
          textarea.style.height = `${Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight)}px`;
        }
      });
    },
    focus: () => {
      textareaRef.current?.focus();
    },
  }));



  const isLarge = size === 'large';
  const minHeight = isLarge ? 60 : 24;
  const maxHeight = isLarge ? 200 : 200;



  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight)}px`;
    }
  }, [value, minHeight, maxHeight]);

  useEffect(() => {
    const handleFocusInput = (event: Event) => {
      const detail = (event as CustomEvent<{ clear?: boolean }>).detail;
      const shouldClear = detail?.clear ?? true;
      if (shouldClear) {
        setValue('');
        setAttachments([]);
      }
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    };
    window.addEventListener('cowork:focus-input', handleFocusInput);
    return () => {
      window.removeEventListener('cowork:focus-input', handleFocusInput);
    };
  }, []);

  useEffect(() => {
    if (workingDirectory?.trim()) {
      setShowFolderRequiredWarning(false);
    }
  }, [workingDirectory]);

  useEffect(() => {
    if (value !== draftPrompt) {
      const timer = setTimeout(() => {
        dispatch(setDraftPrompt(value));
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [value, draftPrompt, dispatch]);

  const handleSubmit = useCallback(() => {
    if (showFolderSelector && !workingDirectory?.trim()) {
      setShowFolderRequiredWarning(true);
      return;
    }

    const trimmedValue = (pendingTranscribedTextRef.current || value).trim();
    // Allow submit if: has text OR has attachments OR has pending audio from recording
    const hasContent = trimmedValue || attachments.length > 0 || pendingAudioRef.current;
    if (!hasContent || isStreaming || disabled) return;
    setShowFolderRequiredWarning(false);

    // If we have transcribed text from voice, use it as the final prompt
    const finalPrompt = pendingTranscribedTextRef.current || trimmedValue;
    pendingTranscribedTextRef.current = null;

    const skillPrompt = undefined;

    // Build image/audio/file attachments from uploaded paths
    const imageAtts: CoworkImageAttachment[] = [];
    const audioAtts: CoworkImageAttachment[] = [];
    const fileAtts: CoworkImageAttachment[] = [];

    for (const attachment of attachments) {
      if (!attachment.serverFilePath) continue;
      const att: CoworkImageAttachment = {
        name: attachment.name,
        mimeType: attachment.mimeType || 'application/octet-stream',
        filePath: attachment.serverFilePath,
        type: attachment.type,
      };
      if (attachment.isImage) {
        imageAtts.push(att);
      } else if (attachment.isAudio) {
        audioAtts.push({ ...att, type: 'audio' });
      } else {
        fileAtts.push({ ...att, type: attachment.type || 'file' });
      }
    }

    // Also check pending audio from recording
    if (pendingAudioRef.current?.serverFilePath) {
      audioAtts.push({
        name: pendingAudioRef.current.name,
        mimeType: pendingAudioRef.current.mimeType || 'audio/webm',
        filePath: pendingAudioRef.current.serverFilePath,
        type: 'audio',
      });
    }
    pendingAudioRef.current = null;

    // Build prompt with non-media attachments that have real file paths.
    // Image/audio/file attachments sent via attachments arrays are handled separately.
    // Note: inline attachments have pseudo-paths starting with 'inline:' and are excluded.
    const attachmentLines = attachments
      .filter((a) => !a.path.startsWith('inline:') && !a.isImage && !a.isAudio && a.type !== 'file')
      .map((attachment) => `${INPUT_FILE_LABEL}: ${attachment.serverFilePath || attachment.path}`)
      .join('\n');
    const promptWithAttachments = finalPrompt
      ? (attachmentLines ? `${finalPrompt}\n\n${attachmentLines}` : finalPrompt)
      : attachmentLines;

    if (imageAtts.length > 0) {
      console.log('[CoworkPromptInput] handleSubmit: passing imageAtts to onSubmit', {
        count: imageAtts.length,
        names: imageAtts.map(a => a.name),
        filePaths: imageAtts.map(a => a.filePath),
      });
    }
    if (audioAtts.length > 0) {
      console.log('[CoworkPromptInput] handleSubmit: passing audioAtts to onSubmit', {
        count: audioAtts.length,
        names: audioAtts.map(a => a.name),
        filePaths: audioAtts.map(a => a.filePath),
      });
    }
    if (fileAtts.length > 0) {
      console.log('[CoworkPromptInput] handleSubmit: passing fileAtts to onSubmit', {
        count: fileAtts.length,
        names: fileAtts.map(a => a.name),
        filePaths: fileAtts.map(a => a.filePath),
      });
    }
    onSubmit(promptWithAttachments, skillPrompt, imageAtts.length > 0 ? imageAtts : undefined, audioAtts.length > 0 ? audioAtts : undefined, fileAtts.length > 0 ? fileAtts : undefined);
    setValue('');
    dispatch(setDraftPrompt(''));
    setAttachments([]);
  }, [value, isStreaming, disabled, onSubmit, attachments, showFolderSelector, workingDirectory, dispatch]);



  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter to submit, Shift+Enter for new line
    const isComposing = event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229;
    if (event.key === 'Enter' && !event.shiftKey && !isComposing && !isStreaming && !disabled) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const handleStopClick = () => {
    if (onStop) {
      onStop();
    }
  };

  const containerClass = isLarge
    ? 'relative rounded-2xl border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-claude-surface shadow-card focus-within:shadow-elevated focus-within:ring-1 focus-within:ring-claude-accent/40 focus-within:border-claude-accent'
    : 'relative flex items-end gap-2 p-3 rounded-xl border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-claude-surface';

  const textareaClass = isLarge
    ? `w-full resize-none bg-transparent px-4 pt-2.5 pb-2 dark:text-claude-darkText text-claude-text placeholder:dark:text-claude-darkTextSecondary/60 placeholder:text-claude-textSecondary/60 focus:outline-none text-[15px] leading-6 min-h-[${minHeight}px] max-h-[${maxHeight}px]`
    : 'flex-1 resize-none bg-transparent dark:text-claude-darkText text-claude-text placeholder:dark:text-claude-darkTextSecondary placeholder:text-claude-textSecondary focus:outline-none text-sm leading-relaxed min-h-[24px] max-h-[200px]';

  const truncatePath = (path: string, maxLength = 30): string => {
    if (!path) return i18nService.t('noFolderSelected');
    return getCompactFolderName(path, maxLength) || i18nService.t('noFolderSelected');
  };

  const handleFolderSelect = (path: string) => {
    if (onWorkingDirectoryChange) {
      onWorkingDirectoryChange(path);
    }
  };

  const addAttachment = useCallback((filePath: string, options?: { isImage?: boolean; isAudio?: boolean; isText?: boolean; serverFilePath?: string; previewDataUrl?: string; mimeType?: string; uploading?: boolean; type?: 'image' | 'audio' | 'text' | 'file'; name?: string }) => {
    if (!filePath) return;
    console.log('[CoworkPromptInput] addAttachment called:', { filePath, options });
    setAttachments((prev) => {
      console.log('[CoworkPromptInput] addAttachment prev:', prev.length, 'attachments');
      if (prev.some((attachment) => attachment.path === filePath)) {
        console.log('[CoworkPromptInput] duplicate attachment, skipping');
        return prev;
      }
      const newAttachment: CoworkAttachment = {
        path: filePath,
        name: options?.name || getFileNameFromPath(filePath),
        isImage: options?.isImage,
        isAudio: options?.isAudio,
        isText: options?.isText,
        serverFilePath: options?.serverFilePath,
        previewDataUrl: options?.previewDataUrl,
        mimeType: options?.mimeType,
        uploading: options?.uploading,
        type: options?.type,
      };
      console.log('[CoworkPromptInput] addAttachment new:', newAttachment);
      return [...prev, newAttachment];
    });
  }, []);



  const handleIncomingFiles = useCallback(async (fileList: FileList | File[]) => {
    if (disabled || isStreaming) return;
    const files = Array.from(fileList ?? []);
    if (files.length === 0) return;

    for (const file of files) {
      const fileIsImage = isImageMimeType(file.type);
      const fileIsAudio = isAudioMimeType(file.type);

      if (!isAllowedFile(file)) {
        console.warn('[CoworkPromptInput] Rejected file type:', file.name, file.type);
        continue;
      }

      // Web mode: add a placeholder first (with uploading state), then upload
      const pseudoPath = `inline:${file.name}:${Date.now()}`;
      const attachType = fileIsImage ? 'image' as const : fileIsAudio ? 'audio' as const : 'file' as const;

      // Add placeholder immediately so user sees feedback
      addAttachment(pseudoPath, {
        name: file.name,
        isImage: fileIsImage,
        isAudio: fileIsAudio,
        mimeType: file.type || 'application/octet-stream',
        type: attachType,
        uploading: true,
      });

      // For images, load a local preview while uploading
      let localPreview: string | undefined;
      if (fileIsImage) {
        try {
          localPreview = await readFileAsDataUrl(file);
        } catch { /* ignore */ }
      }

      try {
        const result = await uploadFiles([file]);
        if (result.rejected && result.rejected.length > 0) {
          console.warn('[CoworkPromptInput] File rejected:', result.rejected[0]);
          setAttachments((prev) => prev.filter((a) => a.path !== pseudoPath));
        } else if (result.files.length > 0 && result.files[0].filePath) {
          const serverFile = result.files[0];
          // Update the attachment with the server path and mark as uploaded
          setAttachments((prev) => prev.map((a) => {
            if (a.path === pseudoPath) {
              return {
                ...a,
                serverFilePath: serverFile.filePath,
                mimeType: serverFile.mimeType || a.mimeType,
                previewDataUrl: localPreview,
                uploading: false,
              };
            }
            return a;
          }));
        } else {
          // Upload returned no file — remove the placeholder
          setAttachments((prev) => prev.filter((a) => a.path !== pseudoPath));
        }
      } catch (error) {
        console.error('Failed to upload file:', error);
        // Remove the placeholder on error
        setAttachments((prev) => prev.filter((a) => a.path !== pseudoPath));
      }
    }
  }, [addAttachment, setAttachments, disabled, isStreaming]);

  const handleAddFile = useCallback(async () => {
    if (isDemoMode()) return;
    if (isAddingFile || disabled || isStreaming) return;
    
    // Web mode: use hidden file input
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '*/*';
    input.onchange = async () => {
      if (input.files && input.files.length > 0) {
        await handleIncomingFiles(input.files);
      }
      setIsAddingFile(false);
    };
    input.click();
  }, [addAttachment, isAddingFile, disabled, isStreaming, handleIncomingFiles]);

  const handleRemoveAttachment = useCallback((path: string) => {
    setAttachments((prev) => prev.filter((attachment) => attachment.path !== path));
  }, []);

  const handleRecordingToggle = useCallback(async () => {
    console.log('[CoworkPromptInput] handleRecordingToggle called, isRecording:', isRecording);
    if (isRecording) {
      // Stop recording and send
      console.log('[CoworkPromptInput] Stopping for send...');
      // Set flag + immediately update UI state so stop/cancel buttons disappear
      isStoppingForSendRef.current = true;
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      setRecordingTime(0);
      if (mediaRecorderRef.current) {
        if (mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.requestData();
          mediaRecorderRef.current.stop();
        } else if (mediaRecorderRef.current.state === 'paused') {
          mediaRecorderRef.current.resume();
          mediaRecorderRef.current.requestData();
          mediaRecorderRef.current.stop();
        }
      }
    } else {
      // Start recording
      if (disabled || isStreaming) return;
      
      // Reset cancel flag
      isStoppingForSendRef.current = false;
      
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
        mediaRecorderRef.current = mediaRecorder;
        
        const audioChunks: Blob[] = [];
        
        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunks.push(event.data);
          }
        };
        
        mediaRecorder.onstop = async () => {
          // Wait a bit for any final chunks to arrive
          await new Promise(resolve => setTimeout(resolve, 100));
          
          const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
          const audioSizeKB = Math.round(audioBlob.size / 1024);
          
          // Only process audio if this was a "send" stop (not cancel)
          // Double-check the ref hasn't been reset by cancel during the 100ms wait
          if (!isStoppingForSendRef.current) {
            // Cancelled — just clean up
            stream.getTracks().forEach(track => track.stop());
            return;
          }
          
          const pseudoPath = `inline:recording_${Date.now()}.webm`;
              console.log(`[CoworkPromptInput] Recording stopped — size: ${audioSizeKB}KB, transcribing via STT...`);
              setIsTranscribing(true);

              try {
                // Upload the audio blob (always keep the recording for playback)
                const uploadStart = Date.now();
                const uploaded = await uploadBlob(audioBlob, pseudoPath, 'audio/webm');
                console.log(`[CoworkPromptInput] Audio uploaded to: ${uploaded.filePath} (${Date.now() - uploadStart}ms)`);

                // Keep the audio as an attachment on the message for playback
                pendingAudioRef.current = { serverFilePath: uploaded.filePath, name: pseudoPath, mimeType: 'audio/webm' };

                // Call unified STT (routes to whisper or google based on STT_PROVIDER)
                const sttStart = Date.now();
                const apiUrl = getApiUrl();
                const token = getAuthToken();
                const sttResponse = await fetch(`${apiUrl}/api/stt/transcribe`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ file: uploaded.filePath, autoDetect: true }),
              });

              if (!sttResponse.ok) {
                const errText = await sttResponse.text();
                throw new Error(`STT failed (${sttResponse.status}): ${errText}`);
              }

              const sttResult = await sttResponse.json();
              const transcribedText = (sttResult.text || '').trim();
              console.log(`[CoworkPromptInput] STT result: "${transcribedText}" | lang: ${sttResult.languageCode} | ${Date.now() - sttStart}ms`);

              if (transcribedText) {
                // Set transcribed text via ref for immediate submit
                pendingTranscribedTextRef.current = transcribedText;
                // Also update the textarea display
                setValue(transcribedText);
                onChange?.(transcribedText);
                // Keep audio attachment for playback — do NOT clear pendingAudioRef
              } else {
                // No transcription — still keep audio attachment, send with placeholder text
                console.warn('[CoworkPromptInput] STT returned empty text, keeping audio attachment');
              }
            } catch (error) {
              console.error('[CoworkPromptInput] STT failed:', error);
              // Audio was already uploaded above; keep it as attachment for playback
            } finally {
              setIsTranscribing(false);
            }

            // Stop stream
            stream.getTracks().forEach(track => track.stop());
            isStoppingForSendRef.current = false;

            // Call handleSubmit directly since we have the data
            console.log('[CoworkPromptInput] Calling handleSubmit after STT');
            handleSubmit();
          };
        
        mediaRecorder.start(100);
        setIsRecording(true);
        setRecordingTime(0);
        
        recordingTimerRef.current = setInterval(() => {
          setRecordingTime(prev => prev + 1);
        }, 1000);
        
      } catch (error) {
        console.error('Failed to start recording:', error);
      }
    }
  }, [isRecording, disabled, isStreaming, addAttachment]);

  const handleCancelRecording = useCallback(() => {
    // Cancel recording without sending
    if (mediaRecorderRef.current && isRecording) {
      // Clear the send flag so onstop won't process audio
      isStoppingForSendRef.current = false;
      // Immediately update UI
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      setRecordingTime(0);
      // Stop the recorder (triggers onstop which will see isStoppingForSend=false → cancel)
      if (mediaRecorderRef.current.state === 'recording' || mediaRecorderRef.current.state === 'paused') {
        mediaRecorderRef.current.stop();
      }
      // Clear any pending audio
      pendingAudioRef.current = null;
      // Remove any attachments that were added
      setAttachments(prev => prev.filter(a => !a.path.startsWith('inline:recording_')));
    }
  }, [isRecording]);

  const formatRecordingTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const hasFileTransfer = (dataTransfer: DataTransfer | null): boolean => {
    if (!dataTransfer) return false;
    if (dataTransfer.files.length > 0) return true;
    return Array.from(dataTransfer.types).includes('Files');
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    if (!hasFileTransfer(event.dataTransfer)) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current += 1;
    if (!disabled && !isStreaming) {
      setIsDraggingFiles(true);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!hasFileTransfer(event.dataTransfer)) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = disabled || isStreaming ? 'none' : 'copy';
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (!hasFileTransfer(event.dataTransfer)) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDraggingFiles(false);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (!hasFileTransfer(event.dataTransfer)) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setIsDraggingFiles(false);
    if (isDemoMode() || disabled || isStreaming) return;
    void handleIncomingFiles(event.dataTransfer.files);
  };

  const handlePaste = useCallback((event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (isDemoMode() || disabled || isStreaming) return;
    const files = Array.from(event.clipboardData?.files ?? []);
    if (files.length === 0) return;
    event.preventDefault();
    void handleIncomingFiles(files);
  }, [disabled, handleIncomingFiles, isStreaming]);

  const canSubmit = !disabled && (!!value.trim() || attachments.length > 0);
  const enhancedContainerClass = isDraggingFiles
    ? `${containerClass} ring-2 ring-claude-accent/50 border-claude-accent/60`
    : containerClass;

  return (
    <div className="relative">
      {attachments.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {attachments.map((attachment) => {
            const isUploading = attachment.uploading && !attachment.serverFilePath;
            const hasPreview = attachment.isImage && attachment.previewDataUrl;
            const ext = attachment.name.includes('.') ? attachment.name.split('.').pop()!.toUpperCase() : '';
            return (
              <div
                key={attachment.path}
                className={`inline-flex items-center gap-1.5 rounded-full border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-claude-surface px-2.5 py-1 text-xs dark:text-claude-darkText text-claude-text max-w-full ${isUploading ? 'opacity-60' : ''}`}
                title={attachment.serverFilePath || attachment.path}
              >
                {isUploading ? (
                  <svg className="h-3.5 w-3.5 flex-shrink-0 animate-spin text-claude-accent" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                ) : hasPreview ? (
                  <img src={attachment.previewDataUrl} alt={attachment.name} className="h-4 w-4 rounded object-cover flex-shrink-0" />
                ) : attachment.isImage ? (
                  <PhotoIcon className="h-3.5 w-3.5 flex-shrink-0 text-blue-500" />
                ) : attachment.isAudio ? (
                  <MicrophoneIcon className="h-3.5 w-3.5 flex-shrink-0 text-red-500" />
                ) : (
                  <DocumentIcon className="h-3.5 w-3.5 flex-shrink-0 text-orange-500" />
                )}
                <span className="truncate max-w-[180px]">{attachment.name}</span>
                {!attachment.isImage && !attachment.isAudio && ext && (
                  <span className="flex-shrink-0 rounded bg-claude-accent/10 px-1 py-0.5 text-[10px] font-medium text-claude-accent">{ext}</span>
                )}
                {!isUploading && (
                  <button
                    type="button"
                    onClick={() => handleRemoveAttachment(attachment.path)}
                    className="ml-0.5 rounded-full p-0.5 hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover"
                    aria-label={i18nService.t('coworkAttachmentRemove')}
                    title={i18nService.t('coworkAttachmentRemove')}
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div
        className={enhancedContainerClass}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDraggingFiles && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-[inherit] bg-claude-accent/10 text-xs font-medium text-claude-accent">
            {i18nService.t('coworkDropFileHint')}
          </div>
        )}
        {isLarge ? (
          <>
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => { setValue(e.target.value); onChange?.(e.target.value); }}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={placeholder}
              disabled={disabled}
              rows={isLarge ? 2 : 1}
              className={textareaClass}
              style={{ minHeight: `${minHeight}px` }}
            />
            <div className="flex items-center justify-between px-4 pb-2 pt-1.5">
              <div className="flex items-center gap-2 relative">
                {showFolderSelector && (
                  <>
                    <div className="relative group">
                      <button
                        ref={folderButtonRef as React.RefObject<HTMLButtonElement>}
                        type="button"
                        onClick={() => setShowFolderMenu(!showFolderMenu)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover dark:hover:text-claude-darkText hover:text-claude-text transition-colors"
                      >
                        <FolderIcon className="h-4 w-4" />
                        <span className="max-w-[150px] truncate text-xs">
                          {truncatePath(workingDirectory)}
                        </span>
                      </button>
                      {/* Tooltip - hidden when folder menu is open */}
                      {!showFolderMenu && (
                        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3.5 py-2.5 text-[13px] leading-relaxed rounded-xl shadow-xl dark:bg-claude-darkBg bg-claude-bg dark:text-claude-darkText text-claude-text dark:border-claude-darkBorder border-claude-border border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none z-50 max-w-[400px] break-all whitespace-nowrap">
                          {truncatePath(workingDirectory, 120)}
                        </div>
                      )}
                    </div>
                    <FolderSelectorPopover
                      isOpen={showFolderMenu}
                      onClose={() => setShowFolderMenu(false)}
                      onSelectFolder={handleFolderSelect}
                      anchorRef={folderButtonRef as React.RefObject<HTMLElement>}
                    />
                  </>
                )}
                {showModelSelector && <ModelSelector dropdownDirection="up" />}
                {!isDemoMode() && (
                  <button
                    type="button"
                    onClick={handleAddFile}
                    className="flex items-center justify-center p-1.5 rounded-lg text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover dark:hover:text-claude-darkText hover:text-claude-text transition-colors"
                    title={i18nService.t('coworkAddFile')}
                    aria-label={i18nService.t('coworkAddFile')}
                    disabled={disabled || isStreaming || isAddingFile}
                  >
                    <PaperClipIcon className="h-4 w-4" />
                  </button>
                )}

              </div>
              <div className="flex items-center gap-2">
                {isRecording ? (
                  <>
                    <button
                      type="button"
                      onClick={handleRecordingToggle}
                      className="p-2 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-all shadow-subtle hover:shadow-card active:scale-95"
                      aria-label="Stop and send recording"
                    >
                      <StopIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelRecording}
                      className="p-1.5 rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover dark:hover:text-claude-darkText hover:text-claude-text transition-colors"
                      title="Cancel recording"
                      aria-label="Cancel recording"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                    <span className="text-xs text-red-500 flex-shrink-0">
                      {formatRecordingTime(recordingTime)}
                    </span>
                  </>
                ) : isTranscribing ? (
                  <span className="text-xs text-blue-500 flex-shrink-0 flex items-center gap-1">
                    <svg className="h-3.5 w-3.5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                    Transcribing...
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleRecordingToggle}
                    className="p-1.5 rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover dark:hover:text-claude-darkText hover:text-claude-text transition-colors"
                    title="Start recording"
                    aria-label="Start recording"
                    disabled={disabled || isStreaming}
                  >
                    <MicrophoneIcon className="h-4 w-4" />
                  </button>
                )}
                {isStreaming ? (
                  <button
                    type="button"
                    onClick={handleStopClick}
                    className="p-2 rounded-xl bg-red-500 hover:bg-red-600 text-white transition-all shadow-subtle hover:shadow-card active:scale-95"
                    aria-label="Stop"
                  >
                    <StopIcon className="h-5 w-5" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    className="p-2 rounded-xl bg-claude-accent hover:bg-claude-accentHover text-white transition-all shadow-subtle hover:shadow-card active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Send"
                  >
                    <PaperAirplaneIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => { setValue(e.target.value); onChange?.(e.target.value); }}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={placeholder}
              disabled={disabled}
              rows={1}
              className={textareaClass}
            />

            <div className="flex items-center gap-1">
              {!isDemoMode() && (
                <button
                  type="button"
                  onClick={handleAddFile}
                  className="flex-shrink-0 p-1.5 rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover dark:hover:text-claude-darkText hover:text-claude-text transition-colors"
                  title={i18nService.t('coworkAddFile')}
                  aria-label={i18nService.t('coworkAddFile')}
                  disabled={disabled || isStreaming || isAddingFile}
                >
                  <PaperClipIcon className="h-4 w-4" />
                </button>
              )}
            </div>

            {isStreaming ? (
              <button
                type="button"
                onClick={handleStopClick}
                className="flex-shrink-0 p-2 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-all shadow-subtle hover:shadow-card active:scale-95"
                aria-label="Stop"
              >
                <StopIcon className="h-4 w-4" />
              </button>
              ) : (
                <>
                  {isRecording ? (
                    <>
                      <button
                        type="button"
                        onClick={handleRecordingToggle}
                        className="flex-shrink-0 p-2 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-all shadow-subtle hover:shadow-card active:scale-95"
                        aria-label="Stop and send recording"
                      >
                        <StopIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelRecording}
                        className="flex-shrink-0 p-1.5 rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover dark:hover:text-claude-darkText hover:text-claude-text transition-colors"
                        title="Cancel recording"
                        aria-label="Cancel recording"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                      <span className="text-xs text-red-500 flex-shrink-0">
                        {formatRecordingTime(recordingTime)}
                      </span>
                    </>
                  ) : isTranscribing ? (
                    <span className="text-xs text-blue-500 flex-shrink-0 flex items-center gap-1">
                      <svg className="h-3.5 w-3.5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                      Transcribing...
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={handleRecordingToggle}
                      className="flex-shrink-0 p-1.5 rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover dark:hover:text-claude-darkText hover:text-claude-text transition-colors"
                      title="Start recording"
                      aria-label="Start recording"
                      disabled={disabled || isStreaming}
                    >
                      <MicrophoneIcon className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    className="flex-shrink-0 p-2 rounded-lg bg-claude-accent hover:bg-claude-accentHover text-white transition-all shadow-subtle hover:shadow-card active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Send"
                  >
                    <PaperAirplaneIcon className="h-4 w-4" />
                  </button>
                </>
              )}
          </>
        )}
      </div>
      {showFolderRequiredWarning && (
        <div className="mt-2 text-xs text-red-500 dark:text-red-400">
          {i18nService.t('coworkSelectFolderFirst')}
        </div>
      )}
    </div>
  );
  }
);

CoworkPromptInput.displayName = 'CoworkPromptInput';

export default CoworkPromptInput;
